// Toastr 2.1.0 appends strings as HTML. Keep its lifecycle/options, but only
// give it nodes whose dynamic content was inserted as text.
const vendor = window.toastr;
const safeNodes = new WeakSet();
const allowedTags = new Set(['br', 'button', 'input', 'label', 'li', 'span', 'ul']);
const allowedAttributes = new Set(['id', 'className', 'type', 'htmlFor']);
let lastMessage;

function element(tag, attributes = {}, ...children) {
    if (!allowedTags.has(tag)) throw new Error('Unsupported toast control');
    const node = document.createElement(tag);
    for (const [name, value] of Object.entries(attributes)) {
        if (!allowedAttributes.has(name)) throw new Error('Unsupported toast attribute');
        if (name === 'type' && value !== 'button' && value !== 'checkbox') throw new Error('Unsupported toast control type');
        if (name === 'id' && !/^[a-zA-Z][a-zA-Z0-9_-]*$/.test(value)) throw new Error('Invalid toast control id');
        node[name] = value;
    }
    children.forEach(child => append(node, child));
    safeNodes.add(node);
    return node;
}

function append(parent, value) {
    if (value === null || value === undefined || value === '') return;
    if (Array.isArray(value)) { value.forEach(part => append(parent, part)); return; }
    parent.appendChild(safeNodes.has(value) ? value : document.createTextNode(String(value)));
}

function content(...parts) {
    const node = element('span', {}, ...parts);
    node.style.whiteSpace = 'pre-line';
    return node;
}

// Only the bundled navigation translations' known links and line breaks are
// interpreted. All other template characters, including unknown tags, are text.
function linkTemplate(template, replacements = []) {
    const result = content();
    const token = /(\$S[1-9]|<br\s*\/?>|<span class="toastr-link-inline" id="(?:shared-offer-menu-link|template-menu-link|submit-a-spec-menu-link)">|<\/span>)/gi;
    let current = result, cursor = 0, match;
    while ((match = token.exec(template)) !== null) {
        append(current, template.slice(cursor, match.index));
        const value = match[0];
        if (value.startsWith('$S')) append(current, replacements[Number(value[2]) - 1] ?? value);
        else if (/^<br/i.test(value)) append(current, element('br'));
        else if (/^<span/i.test(value)) {
            const link = element('span', { id: /id="([^"]+)"/.exec(value)[1], className: 'toastr-link-inline' });
            append(current, link); current = link;
        } else current = result;
        cursor = token.lastIndex;
    }
    append(current, template.slice(cursor));
    return result;
}

// Paragraph translations and missing-values warnings only need line breaks.
// The optional $S1 replacement is a code-owned list; other tags stay text.
function breakTemplate(template, replacement = null) {
    const result = content();
    const token = /(<br\s*\/?>|\$S1)/gi;
    let cursor = 0, match;
    while ((match = token.exec(template)) !== null) {
        append(result, template.slice(cursor, match.index));
        append(result, /^<br/i.test(match[0]) ? element('br') : replacement ?? match[0]);
        cursor = token.lastIndex;
    }
    append(result, template.slice(cursor));
    return result;
}

function asToastNode(value) {
    if (value === null || value === undefined || value === '') return value;
    return window.$(safeNodes.has(value) ? value : content(value));
}

function show(kind, message, title, options) {
    const preventDuplicates = options?.preventDuplicates ?? vendor.options?.preventDuplicates;
    if (preventDuplicates) {
        const container = vendor.getContainer();
        if (!container || !container.length || !container.children().length) lastMessage = undefined;
        const messageKey = safeNodes.has(message) ? message.outerHTML : message;
        if (messageKey === lastMessage) return undefined;
        lastMessage = messageKey;
    }
    return vendor[kind](asToastNode(message), asToastNode(title), options);
}

const safeToastr = {
    element, content, linkTemplate, breakTemplate,
    get options() { return vendor.options; },
    set options(value) { vendor.options = value; },
    info(message, title, options) { return show('info', message, title, options); },
    success(message, title, options) { return show('success', message, title, options); },
    warning(message, title, options) { return show('warning', message, title, options); },
    error(message, title, options) { return show('error', message, title, options); },
    clear(...args) { return vendor.clear(...args); },
    remove(...args) { return vendor.remove(...args); },
    getContainer(...args) { return vendor.getContainer(...args); },
    subscribe(...args) { return vendor.subscribe(...args); }
};

export default safeToastr;
